import React, { useEffect, useRef } from "react";
import { t } from "@lingui/macro";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@shared/api/supabaseClient";
import { queryKeys } from "@shared/lib/react-query";
import { useAuth } from "@shared/hooks/useAuth";
import { Bell } from "lucide-react";

// ─── Event payload shapes ─────────────────────────────────────────────────────

type NotificationCreatedPayload = {
  notificationId: string;
  unreadCount: number;
  category: string;
  priority: number;
  createdAt: string;
  toastEligible: boolean;
};

type NotificationReadPayload = {
  notificationId: string;
  unreadCount: number;
};

type NotificationCountChangedPayload = {
  unreadCount: number;
  trigger: "mark_all_read" | "archive";
};

// ─── Deduplication ────────────────────────────────────────────────────────────
// Module-level set: intentionally survives component remounts so the same
// notification never produces a duplicate toast across navigation events.

const seenNotificationIds = new Set<string>();

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useNotificationRealtime = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const channelRef = useRef<RealtimeChannel | null>(null);

  useEffect(() => {
    if (!enabled || !user?.id) return;

    const topic = `notifications:${user.id}`;
    const channel = supabase.channel(topic, {
      config: { broadcast: { ack: false } },
    });
    channelRef.current = channel;

    channel
      // ── notification.created ─────────────────────────────────────────────
      // Server pushes this after every new notification (or dedupe bump).
      // Invalidate the list + unread count, and optionally show a toast.
      .on("broadcast", { event: "notification.created" }, ({ payload }) => {
        const event = payload as NotificationCreatedPayload;
        if (!event?.notificationId) return;

        // Deduplicate — the same notification can arrive more than once if
        // the user has multiple tabs open (each tab has its own subscription).
        if (seenNotificationIds.has(event.notificationId)) return;
        seenNotificationIds.add(event.notificationId);

        // Sync the badge count from the authoritative server value.
        queryClient.setQueryData(queryKeys.notifications.unreadCount(), {
          unreadCount: event.unreadCount,
        });

        // Invalidate the list so the new item appears when the panel opens.
        void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.lists() });

        if (event.toastEligible) {
          toast(
            t({
              id: "notifications.realtime.new-title",
              message: "New notification",
            }),
            {
              description: t({
                id: "notifications.realtime.new-description",
                message: "Open notifications to review the latest update.",
              }),
              icon: React.createElement(Bell, { className: "mt-0.5 h-5 w-5 text-white/80" }),
              duration: 4000,
            },
          );
        }
      })

      // ── notification.read ────────────────────────────────────────────────
      // Server pushes this when markAsRead is called. Handles cross-tab sync:
      // if the user reads a notification in another tab/window, this updates
      // the current tab's cache without a full refetch.
      .on("broadcast", { event: "notification.read" }, ({ payload }) => {
        const event = payload as NotificationReadPayload;
        if (!event?.notificationId) return;

        // Update the specific item in the infinite list cache.
        queryClient.setQueriesData({ queryKey: queryKeys.notifications.all }, (old: any) => {
          if (!old?.pages) return old;
          const pages = old.pages.map((page: any) => ({
            ...page,
            items: (page.items ?? []).map((item: any) =>
              item.id === event.notificationId
                ? { ...item, isRead: true, readAt: item.readAt ?? new Date().toISOString() }
                : item,
            ),
          }));
          return { ...old, pages };
        });

        // Sync the badge from the server's authoritative count.
        queryClient.setQueryData(queryKeys.notifications.unreadCount(), {
          unreadCount: event.unreadCount,
        });
      })

      // ── notification.count_changed ───────────────────────────────────────
      // Server pushes this after markAllAsRead or archive. Handles cross-tab
      // sync for bulk operations — the list is refetched so archived items
      // disappear and all items show as read.
      .on("broadcast", { event: "notification.count_changed" }, ({ payload }) => {
        const event = payload as NotificationCountChangedPayload;
        if (typeof event?.unreadCount !== "number") return;

        // Sync the badge.
        queryClient.setQueryData(queryKeys.notifications.unreadCount(), {
          unreadCount: event.unreadCount,
        });

        if (event.trigger === "mark_all_read") {
          // Optimistically mark all cached notifications as read so the UI
          // updates instantly without waiting for the refetch.
          queryClient.setQueriesData({ queryKey: queryKeys.notifications.all }, (old: any) => {
            if (!old?.pages) return old;
            const now = new Date().toISOString();
            const pages = old.pages.map((page: any) => ({
              ...page,
              items: (page.items ?? []).map((item: any) =>
                item.isRead ? item : { ...item, isRead: true, readAt: item.readAt ?? now },
              ),
            }));
            return { ...old, pages };
          });
        }

        if (event.trigger === "archive") {
          // An item was archived — invalidate so it disappears from the list.
          void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.lists() });
        }
      })

      .subscribe();

    return () => {
      if (channelRef.current) {
        void supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [enabled, queryClient, user?.id]);
};
